import {EventEmitter, Injectable} from '@angular/core';
import {PlatformCommand, PlatformCommandResponse} from '@shared/commands/command-base';
import {take} from 'rxjs/operators';
import {EventStatus} from '@shared/models/notification-message.model';
import {CommandService} from '@core/services/command.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {DialogService} from '@shared/services/dialog-service';
import {FlowChartDataService} from '@features/data-survey/services/flow-chart-data.service';
import {Store} from '@ngrx/store';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {SnackBarService} from '@core/services/snack-bar.service';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {FlexSheetService} from '@features/spreadsheet/services/flex-sheet.service';
import {Subject} from 'rxjs';
import {GridValueChangeEvent} from '@shared/models/common.models';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {Api} from '@shared/constants/common-field.constants';
import {Clipboard} from '@angular/cdk/clipboard';
import {Dynamic_Grid_Data_Request_Parameters} from '@shared/store/actions/virtual-grid.actions';
import {ParticleCacheService} from '@core/services/particle-cache.service';

@Injectable({
  providedIn: 'root',
})
export class PlatformToolSandboxHandler implements IPlatformToolHandler {

  magicLinkModel: MagicLinkModel;
  selectedTabModel: TabModel;
  ngUnsubscribe = new Subject<void>();
  magicLinkModelValueChanged: EventEmitter<any> = new EventEmitter<any>();
  invalidateSubject: InvalidateSubject;
  flexSheetService: FlexSheetService;

  constructor(private httpClient: HttpClient,
              private commandService: CommandService,
              private router: Router,
              private dialogService: DialogService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private flowchartDataService: FlowChartDataService,
              protected store: Store<IQueryState>,
              private particleCacheService: ParticleCacheService,
              private snackBarService: SnackBarService,
              private clipboard: Clipboard
  ) {
    this.invalidateSubject = new InvalidateSubject();
  }

  executeCommand(command: PlatformCommand) {
    command = JSON.parse(JSON.stringify(command));
    switch (command.name) {
      case 'ToggleGridFilterByArchiveCommand':
        const updateViewCommand = new PlatformCommand('SandBoxToolLinkCommand', Api.AdminPanel);
        updateViewCommand.fieldValues['ShowArchivedSandbox'] = command.isChecked;
        this.commandService.executePlatformCommand(updateViewCommand).pipe(take(1)).subscribe((response: any) => {
            if (response.fieldValues['PlatformLinkModel']) {
              this.magicLinkModel = response.fieldValues['PlatformLinkModel'];
              const sourceType = this.magicLinkModel.tabBar.tabs[0].tabData['SourceType'];
              const params = this.particleCacheService.getUserPreferenceGridSetting(Dynamic_Grid_Data_Request_Parameters, sourceType);
              params['ShowArchivedSandbox'] = command.isChecked;
              this.particleCacheService.saveUserPreferenceGridSetting(Dynamic_Grid_Data_Request_Parameters, sourceType, params);
              this.refreshPlatformLink(response);
            } else {
              console.log('Platform link model not found');
              this.fuseSplashScreenService.hide();
            }
          },
          error => {
            this.fuseSplashScreenService.hide();
            console.error('Operation failed!!!', error);
          });
        break;
      default:
        this.executeCommandApi(command);
        break;
    }
  }

  private refreshPlatformLink(response: PlatformCommandResponse) {
    this.magicLinkModel = response.fieldValues['PlatformLinkModel'];
    this.magicLinkModelValueChangedHandler({
      id: 'PlatformToolContainerComponent', value: response.fieldValues['PlatformLinkModel'],
      magicLinkModel: this.magicLinkModel
    });
  }

  private magicLinkModelValueChangedHandler(command: any) {
    this.magicLinkModelValueChanged.emit(command);
  }

  executeCommandApi(command: PlatformCommand) {
    command = JSON.parse(JSON.stringify(command));
    console.log('[Sandbox] Executing PlatformCommand : ', command.name);
    if (command.api && command.api.length > 0 && !command.isDisabled) {
      this.fuseSplashScreenService.show();

      this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(commandResponse => {
          console.log('[Sand Box] Command Response', commandResponse);
          this.fuseSplashScreenService.hide();

          if (commandResponse.fieldValues['SnackBarMessage']) {
            if (commandResponse.status === EventStatus.Success) {
              this.snackBarService.showInformation(commandResponse.fieldValues['SnackBarMessage']);
            } else {
              this.snackBarService.showError(commandResponse.fieldValues['SnackBarMessage']);
            }
          }

          if (commandResponse.fieldValues['ShowWarning']) {
            this.snackBarService.showWarning(commandResponse.fieldValues['ShowWarning']);
          }
          if (commandResponse.fieldValues['ShowErrorMessage']) {
            this.snackBarService.showError(commandResponse.fieldValues['ShowErrorMessage']);
          }
          if (commandResponse.fieldValues['RedirectCommand']) {
            this.executeCommand(commandResponse.fieldValues['RedirectCommand']);
            return;
          } else if (commandResponse.fieldValues['RedirectUrl']) {
            this.router.navigateByUrl(commandResponse.fieldValues['RedirectUrl']);
            return;
          } else {
            this.invalidateSubject.invalidate();
          }
        },
        error => {
          console.error('Failed ToolBarComponent command', error);
          this.fuseSplashScreenService.hide();
        });
    }
  }

  private handleActions($event: GridValueChangeEvent) {
    switch ($event.action.id) {
      case 'SendLink':
        const command = this.magicLinkModel?.additionalCommands['SendLinkCommand'];
        command.fieldValues['SandboxId'] = $event.item.id;
        this.executeCommand(command);
        break;
      case 'CopyLink':
        const link = $event.item.cells['MagicLinkUrl'].value;
        this.clipboard.copy(link);
        this.snackBarService.showInformation('Link Copied to clipboard');
        break;
      case 'Archive':
        const archiveCommand = this.magicLinkModel?.additionalCommands['ArchiveCommand'];
        archiveCommand.fieldValues['SandboxId'] = $event.item.id;
        this.executeCommand(archiveCommand);
        break;
      case 'Unarchive':
        const unarchiveCommand = this.magicLinkModel?.additionalCommands['ArchiveCommand'];
        unarchiveCommand.fieldValues['SandboxId'] = $event.item.id;
        this.executeCommand(unarchiveCommand);
        break;
      case 'Edit':
        this.openEditor($event);
        break;
      default:
        break;
    }
  }


  private openEditor(event: any, type: string = '') {
    const sourceType = this.magicLinkModel.tabBar.tabs[0].tabData['SourceType'];
    const minWidth = '800px';
    const maxWidth = '1500px';
    this.dialogService.showCellEditorDialog(event.item, event.fieldDefinition, minWidth, maxWidth,
      sourceType).subscribe(state => {
      if (state.action === 'OK') {
        const value = event.item.cells[event.fieldDefinition.key]?.value;
        let command;

        switch (sourceType) {
          case 'SandboxGrid':
            command = this.magicLinkModel?.additionalCommands['UpdateCommand'];
            command.fieldValues['SandboxId'] = event.item.id;
            break;
          default:
            break;
        }
        const displayText = event.item.cells[event.fieldDefinition.key]?.displayText;
        command.fieldValues['DirtyFieldKey'] = event.fieldDefinition?.key;
        if (displayText) {
          command.fieldValues[event.fieldDefinition.key + 'DisplayText'] = displayText;
        }
        command.fieldValues[event.fieldDefinition.key] = value;
        this.executeCommandApi(command);
      }
      if (state.action === 'OK' && event.fieldDefinition.metaData['SkipUpdate']) {
        this.invalidateSubject.invalidate();
      }
    });
  }

  valueChanged($event) {
    if ($event.action) {
      this.handleActions($event);
      return;
    } else {
      const url = 'admin-panel/sandbox/' + $event.item.id;
      this.router.navigateByUrl(url);
    }

  }

  setSelectedTab(selectedTab: TabModel): void {
    this.selectedTabModel = selectedTab;
  }

  destroy() {
    this.invalidateSubject.close();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
